import { createFileRoute } from "@tanstack/react-router";
import { setMediumCache } from "@/lib/page-cache";
import { lazy, Suspense } from "react";
import { Navbar } from "@/components/landing/Navbar";
import { HeroSection } from "@/components/landing/HeroSection";
import { StatsBar } from "@/components/landing/StatsBar";
import { Reveal } from "@/components/Reveal";
import { MapSearchSection } from "@/components/landing/MapSearchSection";
import { SpecialtySearchSection } from "@/components/landing/SpecialtySearchSection";
import { InsuranceSearchSection } from "@/components/landing/InsuranceSearchSection";

// Below-the-fold sections — lazy load so they don't block first paint / LCP.
const FeaturesSection = lazy(() =>
  import("@/components/landing/FeaturesSection").then((m) => ({ default: m.FeaturesSection })),
);
const HowItWorksSection = lazy(() =>
  import("@/components/landing/HowItWorksSection").then((m) => ({ default: m.HowItWorksSection })),
);
const FounderPreview = lazy(() =>
  import("@/components/landing/FounderPreview").then((m) => ({ default: m.FounderPreview })),
);
const CTASection = lazy(() =>
  import("@/components/landing/CTASection").then((m) => ({ default: m.CTASection })),
);
const SecondarySearch = lazy(() =>
  import("@/components/landing/SecondarySearch").then((m) => ({ default: m.SecondarySearch })),
);
const FooterSection = lazy(() =>
  import("@/components/landing/FooterSection").then((m) => ({ default: m.FooterSection })),
);
const SITE = "https://heallexa.com";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Heallexa | AI Powered Healthcare Search" },
      {
        name: "description",
        content:
          "Your free healthcare search. Find doctors, dentists, chiropractors, and therapists by insurance, city, and availability. Book with zero fees.",
      },
      { property: "og:title", content: "Heallexa | AI Powered Healthcare Search" },
      {
        property: "og:description",
        content:
          "Your free healthcare search. Find doctors, dentists, chiropractors, and therapists by insurance, city, and availability. Book with zero fees.",
      },
      { property: "og:url", content: SITE },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Heallexa | AI Powered Healthcare Search" },
      {
        name: "twitter:description",
        content:
          "Your free healthcare search. Find doctors, dentists, chiropractors, and therapists by insurance, city, and availability. Book with zero fees.",
      },
    ],
    links: [{ rel: "canonical", href: `${SITE}/` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${SITE}/#webpage`,
          url: SITE,
          name: "Heallexa | AI Powered Healthcare Search",
          description:
            "Your free healthcare search. Find doctors, dentists, chiropractors, and therapists by insurance, city, and availability. Book with zero fees.",
          isPartOf: { "@id": `${SITE}/#website` },
          about: { "@id": `${SITE}/#organization` },
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: ["h1"],
          },
        }),
      },
    ],
  }),
  loader: async () => {
    await setMediumCache();
  },
  staleTime: 15 * 60 * 1000,
  errorComponent: () => <Index />,
  component: Index,
});

function Index() {
  return (
    <div className="min-h-screen bg-background pb-1">
      <Navbar />
      <main>
        <HeroSection />
        <Reveal from="up" delay={40}>
          <StatsBar />
        </Reveal>
        <MapSearchSection />
        <SpecialtySearchSection />
        <InsuranceSearchSection />
        <Suspense fallback={null}>
          <FeaturesSection />
          <HowItWorksSection />
          <FounderPreview />
          <SecondarySearch />
          <CTASection />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <FooterSection />
      </Suspense>
    </div>
  );
}
